import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MaskLines } from "@/components/site/motion";
import { CONTACT } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollToForm = () =>
    document.getElementById("admissions")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      className="relative min-h-[92svh] grain overflow-hidden bg-forestdark"
    >
      {/* Parallax hero video — no poster to avoid flash on load */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0 bg-forestdark">
        <video
          src="/hero.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-label="Ambient nature footage — a peaceful path evoking new beginnings"
          className="w-full h-full object-cover object-center"
        />
        {/* Cinematic layered overlays for depth + legibility on the card side */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/30 to-ink/15" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-forestdark/70" />
      </motion.div>

      {/* Content grid */}
      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 min-h-[92svh] flex items-center pt-24 pb-16"
      >
        {/* Glass card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          data-testid="hero-glass-card"
          className="w-full max-w-2xl rounded-2xl border border-white/25 bg-white/[0.08] backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] p-6 sm:p-8 md:p-10"
        >
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="text-white/85 uppercase tracking-[0.28em] text-[11px] md:text-xs font-semibold mb-5"
          >
            Private-pay &amp; GreenShield · Same-day Admission · Toronto
          </motion.p>

          <h1 className="font-serif text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            <MaskLines lines={["Inpatient Drug & Alcohol"]} delay={0.3} />
            <MaskLines
              lines={["Rehab in Toronto"]}
              delay={0.45}
              className="italic text-terracotta drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-6 text-white/90 text-[15px] md:text-base leading-relaxed max-w-lg"
          >
            A licensed inpatient drug &amp; alcohol addiction and mental-health treatment centre
            near the Scarborough Bluffs. Compassionate, evidence-based residential rehab — a new
            path, a new life.
          </motion.p>

          {/* Phone number CTA row like the old site */}
          <motion.a
            href={CONTACT.phoneHref}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            data-testid="hero-phone-large"
            className="mt-6 inline-flex items-center gap-3 text-white group"
          >
            <span className="grid place-items-center w-9 h-9 rounded-full bg-terracotta/90 group-hover:bg-terracotta transition-colors">
              <Phone className="w-4 h-4 text-white" />
            </span>
            <span className="font-serif text-2xl md:text-3xl tracking-tight underline-offset-4 group-hover:underline">
              {CONTACT.phone}
            </span>
          </motion.a>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <Button
              onClick={scrollToForm}
              data-testid="hero-get-help"
              className="h-12 px-7 rounded-full bg-terracotta hover:bg-[#a8664b] text-white text-sm md:text-base font-semibold active:scale-[0.98] transition-colors"
            >
              Get Help Today
            </Button>
            <a
              href="#admissions"
              data-testid="hero-message"
              onClick={(e) => {
                e.preventDefault();
                scrollToForm();
              }}
              className="h-12 px-7 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white flex items-center justify-center gap-2 font-semibold text-sm md:text-base transition-colors"
            >
              Message Us
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-black/25 border border-white/15 px-4 py-2 text-white/90 text-xs sm:text-sm font-medium"
          >
            <ShieldCheck className="w-4 h-4 text-terracotta shrink-0" />
            Immediate admittance &amp; shorter wait lists · Private-pay &amp; GreenShield clients are welcome
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="mt-4 flex flex-wrap items-center gap-2 text-white/75 text-[12px]"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" /> Located in Toronto, ON
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 font-semibold tracking-[0.15em] text-[10px]">
              CARF
            </span>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/75 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
